import { useState, useEffect } from "react";
import { environment } from "../../environment";
import {
  resolveDeploymentBanner,
  type DeploymentBannerInfo,
} from "../deploymentEnvironment";
import s from "./EnvironmentBanner.module.css";

/**
 * Persistent, non-dismissible shell banner showing the Pacific deployment
 * environment (not the in-flow Christmas/MyPay company environment).
 *
 * Colour tiers use Design System semantic tokens closest to Christmas:
 * Production blue, Sandbox/UAT orange, Dev/Preview/Local green.
 */
export function EnvironmentBanner() {
  const [info, setInfo] = useState<DeploymentBannerInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    environment()
      .then((env) => {
        if (!cancelled) {
          setInfo(resolveDeploymentBanner(env.DEPLOYMENT_ENVIRONMENT));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInfo(resolveDeploymentBanner(""));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Avoid a false "Unknown environment" flash while config loads.
  if (!info) {
    return null;
  }

  const message =
    info.colourTier === "unknown"
      ? info.label
      : `You are in ${info.label}`;

  return (
    <div
      className={s.banner}
      data-colour-tier={info.colourTier}
      role="status"
      aria-live="polite"
    >
      <span className={s.label}>{message}</span>
    </div>
  );
}
